import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";

export default function ResetPasswordPage() {
  const [password, setPassword] = useState("");
  const [password2, setPassword2] = useState("");
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState<"checking" | "ready" | "invalid">("checking");
  const navigate = useNavigate();
  const { toast } = useToast();

  useEffect(() => {
    let active = true;

    const { data: sub } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session && active) setStatus("ready");
    });

    const init = async () => {
      const url = new URL(window.location.href);
      const hash = new URLSearchParams(url.hash.replace(/^#/, ""));

      // Fehler direkt aus dem Link (abgelaufen, schon benutzt)
      if (url.searchParams.get("error") || hash.get("error")) {
        if (active) setStatus("invalid");
        return;
      }

      // PKCE-Link: ?code=...
      const code = url.searchParams.get("code");
      if (code) {
        const { error } = await supabase.auth.exchangeCodeForSession(code);
        if (!active) return;
        setStatus(error ? "invalid" : "ready");
        return;
      }

      // Klassischer Link: #access_token=...
      const accessToken = hash.get("access_token");
      const refreshToken = hash.get("refresh_token");
      if (accessToken && refreshToken) {
        const { error } = await supabase.auth.setSession({
          access_token: accessToken,
          refresh_token: refreshToken,
        });
        if (!active) return;
        setStatus(error ? "invalid" : "ready");
        return;
      }

      const { data } = await supabase.auth.getSession();
      if (!active) return;
      setStatus(data.session ? "ready" : "invalid");
    };

    init();

    return () => {
      active = false;
      sub.subscription.unsubscribe();
    };
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password.length < 6) {
      toast({ title: "Zu kurz", description: "Mindestens 6 Zeichen.", variant: "destructive" });
      return;
    }
    if (password !== password2) {
      toast({ title: "Passwörter stimmen nicht überein", description: "Bitte beide Felder gleich ausfüllen.", variant: "destructive" });
      return;
    }
    setLoading(true);
    const { error } = await supabase.auth.updateUser({ password });
    setLoading(false);
    if (error) {
      toast({ title: "Fehler", description: error.message, variant: "destructive" });
      return;
    }
    toast({ title: "Passwort geändert", description: "Du bist jetzt angemeldet." });
    navigate("/mein-verein");
  };

  return (
    <div className="flex min-h-[calc(100vh-4rem)] items-center justify-center px-4">
      <div className="w-full max-w-md rounded-lg border border-border bg-card p-8">
        <h1 className="font-display text-2xl font-bold uppercase tracking-tight text-foreground">Neues Passwort</h1>

        {status === "invalid" ? (
          <>
            <p className="mt-3 text-sm text-muted-foreground">
              Dieser Link ist abgelaufen oder wurde bereits benutzt. Fordere einfach einen neuen an.
            </p>
            <Button asChild variant="hero" className="mt-6 w-full">
              <Link to="/passwort-vergessen">Neuen Link anfordern</Link>
            </Button>
          </>
        ) : (
          <>
            <p className="mt-1 text-sm text-muted-foreground">
              {status === "ready" ? "Wähle ein neues Passwort für dein Konto." : "Link wird geprüft..."}
            </p>
            <form onSubmit={handleSubmit} className="mt-6 space-y-4">
              <div>
                <Label htmlFor="password">Neues Passwort</Label>
                <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" required className="mt-1" />
              </div>
              <div>
                <Label htmlFor="password2">Passwort wiederholen</Label>
                <Input id="password2" type="password" value={password2} onChange={(e) => setPassword2(e.target.value)} placeholder="••••••••" required className="mt-1" />
              </div>
              <Button type="submit" variant="hero" className="w-full" disabled={loading || status !== "ready"}>
                {loading ? "Wird gespeichert..." : "Passwort speichern"}
              </Button>
            </form>
          </>
        )}

        <p className="mt-6 text-center text-sm text-muted-foreground">
          <Link to="/login" className="text-primary hover:underline">Zurück zum Login</Link>
        </p>
      </div>
    </div>
  );
}
